import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { RootState } from '../index';
import { getApiUrl } from '../../config/api';

export interface Tag {
  tag_name: string;
  contact_count: number;
}

export interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  tag_name: string;
}
 
export interface Campaign {
  id: number;
  user_id: number;
  agent_id: number;
  umbrella_account_id: number;
  name: string;
  tag_name: string;
  status: 'active' | 'paused' | 'stopped' | 'completed';
  interval_minutes: number;
  timezone: string;
  start_hour: number;
  end_hour: number;
  max_call_duration_minutes: number;
  max_retries: number;
  days_of_week: string[];
  total_contacts: number;
  completed_contacts: number;
  failed_contacts: number;
  current_contact_index: number;
  created_at: string;
  updated_at?: string;
  started_at?: string;
  completed_at?: string;
  paused_at?: string;
}

export interface CampaignCreate {
  name: string;
  tag_name: string;
  agent_id: number;
  umbrella_account_id: number;
  interval_minutes: number;
  timezone: string;
  start_hour: number;
  end_hour: number;
  max_call_duration_minutes: number;
  max_retries: number;
  days_of_week: string[];
}

export interface CampaignStatus {
  campaign: Campaign;
  current_contact?: {
    id: number;
    first_name: string;
    last_name: string;
    phone: string;
    current_attempt: number;
  };
  recent_calls: any[];
}

export interface CallLog {
  id: number;
  campaign_id: number;
  contact_id: number;
  agent_id: number;
  attempt_number: number;
  synthflow_call_id?: string;
  phone_number: string;
  call_status?: string;
  call_duration_seconds?: number;
  call_started_at?: string;
  call_ended_at?: string;
  transcript?: string;
  recording_url?: string;
  error_message?: string;
  created_at: string;
}

export interface CampaignContact {
  id: number;
  umbrella_contact_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  current_attempt: number;
  final_status: string;
  last_call_at?: string;
  created_at: string;
}

export const outboundCallingApi = createApi({
  reducerPath: 'outboundCallingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl('/api/v1/outbound-calling'),
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        console.log('🔑 OutboundCalling API: Added auth token to headers');
      } else {
        console.warn('⚠️ OutboundCalling API: No auth token found in state');
      }
      return headers;
    },
  }),
  tagTypes: ['Campaign', 'Contact', 'CallLog'], // Removed 'Tag' to prevent caching
  endpoints: (builder) => ({
    // Get tags from Umbrella DB - NO CACHING AT ALL
    getTags: builder.query<Tag[], string | undefined>({
      query: (umbrellaUserId) => {
        const id = umbrellaUserId || '';
        return `/debug/tags-no-auth?umbrella_user_id=${id}&t=${Date.now()}&r=${Math.random()}`;
      },
      // Completely disable all caching
      keepUnusedDataFor: 0,
    }),

    // Get contacts by tag
    getContactsByTag: builder.query<Contact[], string>({
      query: (tagName) => `/tags/${tagName}/contacts`,
      providesTags: ['Contact'],
    }),

    // Get call logs for contact by ID
    getContactCallLogs: builder.query<CallLog[], number>({
      query: (contactId) => `/contacts/${contactId}/call-logs`,
      providesTags: ['CallLog'],
    }),

    // Get call logs for contact by phone number
    getContactCallLogsByPhone: builder.query<CallLog[], string>({
      query: (phoneNumber) => `/contacts/phone/${phoneNumber}/call-logs`,
      providesTags: ['CallLog'],
    }),

    // Get active campaign for agent
    getActiveCampaign: builder.query<Campaign | null, number>({
      query: (agentId) => `/campaigns/active?agent_id=${agentId}`,
      providesTags: ['Campaign'],
    }),

    // Get all campaigns
    getCampaigns: builder.query<Campaign[], { agentId?: number; status?: string; accountId?: number }>({
      query: ({ agentId, status, accountId }) => {
        const params = new URLSearchParams();
        if (agentId) params.append('agent_id', agentId.toString());
        if (status) params.append('status', status);
        if (accountId) params.append('account_id', accountId.toString());
        const queryString = params.toString();
        const fullUrl = `/campaigns?${queryString}`;
        console.log('🔍 getCampaigns API call:', {
          agentId,
          status,
          accountId,
          queryString,
          fullUrl,
          baseUrl: getApiUrl('/api/v1/outbound-calling'),
          completeUrl: `${getApiUrl('/api/v1/outbound-calling')}${fullUrl}`
        });
        return fullUrl;
      },
      providesTags: ['Campaign'],
    }),

    // Get campaign details
    getCampaign: builder.query<Campaign, number>({
      query: (campaignId) => `/campaigns/${campaignId}`,
      providesTags: ['Campaign'],
    }),

    // Get campaign status
    getCampaignStatus: builder.query<CampaignStatus, number>({
      query: (campaignId) => `/campaigns/${campaignId}/status`,
      providesTags: ['Campaign'],
    }),

    // Create campaign
    createCampaign: builder.mutation<Campaign, CampaignCreate>({
      query: (campaign) => ({
        url: '/campaigns',
        method: 'POST',
        body: campaign,
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Start campaign
    startCampaign: builder.mutation<{ message: string }, number>({
      query: (campaignId) => ({
        url: `/campaigns/${campaignId}/start`,
        method: 'PUT',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Pause campaign
    pauseCampaign: builder.mutation<{ message: string }, number>({
      query: (campaignId) => ({
        url: `/campaigns/${campaignId}/pause`,
        method: 'PUT',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Resume campaign
    resumeCampaign: builder.mutation<{ message: string }, number>({
      query: (campaignId) => ({
        url: `/campaigns/${campaignId}/resume`,
        method: 'PUT',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Stop campaign
    stopCampaign: builder.mutation<{ message: string }, number>({
      query: (campaignId) => ({
        url: `/campaigns/${campaignId}/stop`,
        method: 'PUT',
      }),
      invalidatesTags: ['Campaign'],
    }),

    // Sync call details for campaign
    syncCampaignCallDetails: builder.mutation<{
      message: string;
      synced_count: number;
      failed_count: number;
      updated_contacts: number;
      total_call_logs: number;
    }, number>({
      query: (campaignId) => ({
        url: `/campaigns/${campaignId}/sync-call-details`,
        method: 'POST',
      }),
      invalidatesTags: ['Campaign', 'Contact', 'CallLog'],
    }),

    // Get campaign contacts
    getCampaignContacts: builder.query<CampaignContact[], number>({
      query: (campaignId) => `/campaigns/${campaignId}/contacts`,
      providesTags: ['Contact'],
    }),

    // Get campaign call logs
    getCampaignCallLogs: builder.query<CallLog[], number>({
      query: (campaignId) => `/campaigns/${campaignId}/call-logs`,
      providesTags: ['CallLog'],
    }),
  }),
});

export const {
  useGetTagsQuery,
  useGetContactsByTagQuery,
  useGetContactCallLogsQuery,
  useGetContactCallLogsByPhoneQuery,
  useGetActiveCampaignQuery,
  useGetCampaignsQuery,
  useGetCampaignQuery,
  useGetCampaignStatusQuery,
  useCreateCampaignMutation,
  useStartCampaignMutation,
  usePauseCampaignMutation,
  useResumeCampaignMutation,
  useStopCampaignMutation,
  useSyncCampaignCallDetailsMutation,
  useGetCampaignContactsQuery,
  useGetCampaignCallLogsQuery,
} = outboundCallingApi;
